import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import {
  clearTextTestimonialStatus,
  getTextTestimonial,
  removeTextTestimonial,
  updateTextTestimonial,
} from 'slice/textTestimonialSlice';

const ensureTrailingSlash = (value = '') => (value.endsWith('/') ? value : `${value}/`);

const isAbsoluteUrl = (value = '') => /^https?:\/\//i.test(value);

const isManagedUploadPath = (value = '') =>
  typeof value === 'string' && value.replace(/\\/g, '/').startsWith('text_testimonials/');

const isActiveValue = (value) => {
  if (value === 0 || value === false) {
    return false;
  }

  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized !== '0' && normalized !== 'false' && normalized !== 'inactive' && normalized !== 'no';
};

const getProfileImageUrl = (value = '') => {
  if (!value) {
    return '';
  }

  if (isAbsoluteUrl(value)) {
    return value;
  }

  const normalized = String(value).replace(/\\/g, '/').replace(/^\/+/, '');
  const uploadsBase = process.env.REACT_APP_BACKEND_URL_REL
    ? ensureTrailingSlash(process.env.REACT_APP_BACKEND_URL_REL)
    : '';

  if (uploadsBase && isManagedUploadPath(normalized)) {
    return `${uploadsBase}${normalized}`;
  }

  const backendBase = process.env.REACT_APP_BACKEND ? ensureTrailingSlash(process.env.REACT_APP_BACKEND) : '';
  return backendBase ? `${backendBase}${normalized}` : normalized;
};

const getInitialFormValues = (testimonial = {}) => ({
  full_name: testimonial.full_name || '',
  review_text: testimonial.review_text || '',
  rating: Number(testimonial.rating) || 5,
  review_time: testimonial.review_time || '1 year ago',
  is_active: isActiveValue(testimonial.is_active) ? 1 : 0,
  profile_image_url: isManagedUploadPath(testimonial.profile_image) ? '' : testimonial.profile_image || '',
  profile_image_file: null,
});

const List = () => {
  const dispatch = useDispatch();
  const { data, error, lastAction, loading } = useSelector((state) => state.textTestimonial);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [formData, setFormData] = useState(() => getInitialFormValues({}));

  useEffect(() => {
    dispatch(getTextTestimonial());
  }, [dispatch]);

  useEffect(() => {
    if (pendingDeleteId && lastAction === 'remove' && !loading && !error) {
      toast('Text testimonial deleted successfully!');
      dispatch(clearTextTestimonialStatus());
      setPendingDeleteId(null);
    }
  }, [dispatch, error, lastAction, loading, pendingDeleteId]);

  useEffect(() => {
    if (hasSubmitted && lastAction === 'update' && !loading && !error) {
      toast('Text testimonial updated successfully!');
      dispatch(clearTextTestimonialStatus());
      setHasSubmitted(false);
      setShowModal(false);
      setCurrentTestimonial(null);
      setRemoveExistingImage(false);
      setFormData(getInitialFormValues({}));
    }
  }, [dispatch, error, hasSubmitted, lastAction, loading]);

  useEffect(() => {
    if (!formData.profile_image_file) {
      setProfileImagePreview('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.profile_image_file);
    setProfileImagePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.profile_image_file]);

  const handleEditClick = (testimonial) => {
    dispatch(clearTextTestimonialStatus());
    setCurrentTestimonial(testimonial);
    setFormData(getInitialFormValues(testimonial));
    setRemoveExistingImage(false);
    setHasSubmitted(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    dispatch(clearTextTestimonialStatus());
    setShowModal(false);
    setCurrentTestimonial(null);
    setHasSubmitted(false);
    setRemoveExistingImage(false);
    setFormData(getInitialFormValues({}));
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: name === 'rating' || name === 'is_active' ? Number(value) : value,
    }));

    if (name === 'profile_image_url') {
      setRemoveExistingImage(false);
    }
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      profile_image_file: file,
    }));
    setRemoveExistingImage(false);
  };

  const handleRemoveImage = () => {
    setRemoveExistingImage(true);
    setProfileImagePreview('');
    setFormData((prevState) => ({
      ...prevState,
      profile_image_file: null,
      profile_image_url: '',
    }));
  };

  const handleSave = () => {
    if (!currentTestimonial) {
      return;
    }

    if (!formData.full_name.trim()) {
      toast('Full name is required.');
      return;
    }

    if (!formData.review_text.trim()) {
      toast('Review text is required.');
      return;
    }

    const payload = new FormData();
    payload.append('full_name', formData.full_name.trim());
    payload.append('review_text', formData.review_text.trim());
    payload.append('rating', String(formData.rating));
    payload.append('review_time', formData.review_time.trim() || '1 year ago');
    payload.append('is_active', String(formData.is_active));

    if (formData.profile_image_file) {
      payload.append('profile_image', formData.profile_image_file);
    } else if (formData.profile_image_url.trim()) {
      payload.append('profile_image', formData.profile_image_url.trim());
    } else if (removeExistingImage) {
      payload.append('profile_image', '');
    }

    setHasSubmitted(true);
    dispatch(updateTextTestimonial({ id: currentTestimonial.id, data: payload }));
  };

  const currentProfileImageUrl =
    profileImagePreview ||
    getProfileImageUrl(formData.profile_image_url.trim()) ||
    (!removeExistingImage && isManagedUploadPath(currentTestimonial?.profile_image)
      ? getProfileImageUrl(currentTestimonial.profile_image)
      : '');

  const tableHeaders = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Profile',
      accessor: 'profile_image',
      Cell: ({ row }) =>
        row.original.profile_image ? (
          <img
            src={getProfileImageUrl(row.original.profile_image)}
            alt={row.original.full_name || 'testimonial'}
            width={70}
            height={70}
            className="img-thumbnail"
            style={{ objectFit: 'cover', borderRadius: '50%' }}
          />
        ) : (
          <span className="text-muted">No image</span>
        ),
    },
    {
      Header: 'Name',
      accessor: 'full_name',
    },
    {
      Header: 'Review',
      accessor: 'review_text',
      Cell: ({ row }) => <TruncatedMessage text={row.original.review_text} maxLength={60} />,
    },
    {
      Header: 'Rating',
      accessor: 'rating',
      Cell: ({ row }) => `${row.original.rating || 0} / 5`,
    },
    {
      Header: 'Review Time',
      accessor: 'review_time',
    },
    {
      Header: 'Status',
      accessor: 'is_active',
      Cell: ({ row }) => (
        <span className={`badge ${isActiveValue(row.original.is_active) ? 'badge-success' : 'badge-secondary'}`}>
          {isActiveValue(row.original.is_active) ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button
            className="btn removebtn mx-1 mb-2"
            onClick={() => {
              setPendingDeleteId(row.original.id);
              dispatch(removeTextTestimonial(row.original.id));
            }}
          >
            Remove
          </button>
          <button className="btn editbtn mx-1 mb-2" onClick={() => handleEditClick(row.original)}>
            Edit
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Text Testimonial List</h4>
        </div>

        {Array.isArray(data) && data.length > 0 ? (
          <AdminTable tableHeaders={tableHeaders} tableData={data} />
        ) : (
          <p className="mx-4 mt-4">{loading ? 'Loading text testimonials...' : 'No text testimonials available.'}</p>
        )}
      </div>

      {showModal && currentTestimonial && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Text Testimonial</h5>
                <button type="button" className="close" onClick={handleCloseModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <form>
                  <div className="form-group">
                    <label htmlFor="editTestimonialName">Full Name</label>
                    <input
                      id="editTestimonialName"
                      name="full_name"
                      type="text"
                      className="form-control"
                      value={formData.full_name}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editTestimonialRating">Rating</label>
                    <select
                      id="editTestimonialRating"
                      name="rating"
                      className="form-control"
                      value={formData.rating}
                      onChange={handleChange}
                    >
                      {[5, 4, 3, 2, 1].map((rating) => (
                        <option key={rating} value={rating}>
                          {rating} / 5
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="editTestimonialStatus">Status</label>
                    <select
                      id="editTestimonialStatus"
                      name="is_active"
                      className="form-control"
                      value={formData.is_active}
                      onChange={handleChange}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="editTestimonialReviewTime">Review Time</label>
                    <input
                      id="editTestimonialReviewTime"
                      name="review_time"
                      type="text"
                      className="form-control"
                      value={formData.review_time}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editTestimonialReviewText">Review Text</label>
                    <textarea
                      id="editTestimonialReviewText"
                      name="review_text"
                      rows={5}
                      className="form-control"
                      value={formData.review_text}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editTestimonialProfileImageFile">Replace Profile Image</label>
                    <input
                      id="editTestimonialProfileImageFile"
                      type="file"
                      name="profile_image_file"
                      className="form-control btn-block filebg"
                      accept="image/*"
                      onChange={handleProfileImageChange}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="editTestimonialProfileImageUrl">Profile Image URL</label>
                    <input
                      id="editTestimonialProfileImageUrl"
                      name="profile_image_url"
                      type="url"
                      className="form-control"
                      value={formData.profile_image_url}
                      onChange={handleChange}
                    />
                  </div>

                  {currentProfileImageUrl && (
                    <div className="form-group">
                      <div className="mb-2">Current Image</div>
                      <div>
                        <img
                          src={currentProfileImageUrl}
                          alt={formData.full_name || 'testimonial'}
                          className="img-thumbnail"
                          style={{ maxWidth: 220, width: '100%', objectFit: 'cover' }}
                        />
                      </div>
                    </div>
                  )}

                  {currentTestimonial?.profile_image && !removeExistingImage && (
                    <div className="form-group">
                      <button type="button" className="btn editbtn" onClick={handleRemoveImage}>
                        Remove Current Image
                      </button>
                    </div>
                  )}
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Close
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave} disabled={loading}>
                  Save changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default List;
